import React, { useEffect, useState } from 'react';
import { Alert, CircularProgress, Box } from '@mui/material';
import { FollowupDialog, ProgressionValue } from '../../follow-up-on-efforts/followup-dialog';
import { MethodTypeModel } from '../../../../../api-service/clients';
import { moduleClientFactory } from '../../../../../api-service/module-client-factory';
import {
  getServiceProviderPartialObjective,
  createServiceProviderFollowupFromEvent,
  type PartialObjectiveContext,
} from '../followup-from-event-bff-client';

const serviceProviderClient = moduleClientFactory.createCitizenServiceProviderClient(true);

interface CreateFollowupFromEventDialogProps {
  citizenId: string;
  eventId: string;
  partialObjectiveId: string;
  /** The event's start date — becomes the locked follow-up date (EventDate = event.StartAt per contract). */
  eventStartDate: Date;
  onClose: () => void;
  onCreated: () => void;
}

type LoadState = 'loading' | 'error' | 'ready';

export function CreateFollowupFromEventDialog({
  citizenId,
  eventId,
  partialObjectiveId,
  eventStartDate,
  onClose,
  onCreated,
}: CreateFollowupFromEventDialogProps) {
  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [poContext, setPoContext] = useState<PartialObjectiveContext | null>(null);
  const [methodTypes, setMethodTypes] = useState<MethodTypeModel[]>([]);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoadState('loading');
    setPoContext(null);

    getServiceProviderPartialObjective({ citizenId, partialObjectiveId })
      .then(context => {
        if (!cancelled) {
          setPoContext(context);
          setLoadState('ready');
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLoadState('error');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [citizenId, partialObjectiveId]);

  useEffect(() => {
    let cancelled = false;

    serviceProviderClient
      .getActiveMethodTypes()
      .then(response => {
        if (!cancelled) {
          setMethodTypes(response.methodTypes ?? []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setMethodTypes([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  async function handleSave(values: {
    progression: ProgressionValue;
    description: string;
    eventDate: Date;
    methodTypeId?: string;
  }) {
    setSaveError(null);
    try {
      await createServiceProviderFollowupFromEvent({
        citizenId,
        eventId,
        progression: values.progression,
        description: values.description,
        methodType: values.methodTypeId,
      });
      onCreated();
      onClose();
    } catch {
      setSaveError('Kunne ikke oprette opfølgning. Prøv igen.');
    }
  }

  if (loadState === 'loading') {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={120} p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (loadState === 'error') {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Kunne ikke hente delmålsoplysninger. Luk og prøv igen.
      </Alert>
    );
  }

  return (
    <>
      {saveError && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {saveError}
        </Alert>
      )}
      <FollowupDialog
        open
        isEdit={false}
        initialValues={{ eventDate: eventStartDate }}
        methodTypes={methodTypes}
        partialObjectiveMethodType={poContext?.methodType}
        interventionObjectiveTitle={poContext?.interventionObjectiveTitle}
        interventionObjectiveDescription={poContext?.interventionObjectiveDescription}
        partialObjectiveTitle={poContext?.title}
        partialObjectiveDescription={poContext?.description}
        eventDateReadOnly
        onClose={onClose}
        onSave={handleSave}
      />
    </>
  );
}
